import { APP_SETTINGS } from "../../shared/app_settings";
import { CHANNELS } from "../../shared/channels";
import { BotStatus, ServerOverview } from "../../shared/types";
import { call } from "../bridge";
import { byId, cloneTemplate, fillFields, renderList } from "../dom";
import { onViewHidden, onViewShown } from "../navigation";
import { formatBytes, timeSince } from "../utils";
import { setNeedle } from "./today_view";
import { t } from "../i18n";

// live bot status and controls for the vps, refreshes while the tab is open

let pollTimer = 0;
const CONFIRM_ACTIONS = ["stop", "restart"];

// each action's own words, so every language can bend the verb its way
function actionText(action: string, name: string): { ask: string; doing: string; sent: string } {
  if (action === "start") return { ask: t("start {name}?", { name }), doing: t("starting {name}...", { name }), sent: t("{name} start sent", { name }) };
  if (action === "stop") return { ask: t("stop {name}?", { name }), doing: t("stopping {name}...", { name }), sent: t("{name} stop sent", { name }) };
  if (action === "restart") return { ask: t("restart {name}?", { name }), doing: t("restarting {name}...", { name }), sent: t("{name} restart sent", { name }) };
  return { ask: `${action} ${name}?`, doing: `${action}ing ${name}...`, sent: `${name} ${action} sent` };
}

function setStatus(text: string): void {
  byId("server-status").textContent = text;
}

function buildBot(bot: BotStatus): HTMLElement {
  const meta = [t(bot.health), bot.health === "running" ? t("up {time}", { time: timeSince(bot.since) }) : "", formatBytes(bot.memoryBytes), bot.restarts ? t("{count} restarts", { count: bot.restarts }) : ""]
    .filter(Boolean)
    .join(" · ");
  const card = fillFields(cloneTemplate("template-bot"), { name: bot.name, meta });
  card.classList.add(`health-${bot.health.replace(" ", "-")}`);
  card.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      handleAction(bot, button.dataset.action!);
    });
  });
  return card;
}

async function handleAction(bot: BotStatus, action: string): Promise<void> {
  if (action === "logs") return showJournal(bot);
  const text = actionText(action, bot.name);
  if (CONFIRM_ACTIONS.includes(action) && !confirm(text.ask)) return;
  setStatus(text.doing);
  try {
    await call(CHANNELS.serverAction, bot.unit, action);
    setStatus(text.sent);
  } catch (error) {
    setStatus(String(error).replace(/^.*Error: /, ""));
  }
  refresh();
}

async function showJournal(bot: BotStatus): Promise<void> {
  byId("journal-title").textContent = t("{name} logs", { name: bot.name });
  byId("journal-text").textContent = t("loading...");
  byId("server-journal").hidden = false;
  byId("journal-text").textContent = await call<string>(CHANNELS.serverJournal, bot.unit);
}

function renderOverview(overview: ServerOverview): void {
  byId("server-memory").textContent = `${Math.round((overview.memoryUsed / overview.memoryTotal) * 100)}%`;
  byId("server-load").textContent = overview.load.split(" ")[0];
  setNeedle("server-memory", overview.memoryUsed / overview.memoryTotal);
  setNeedle("server-load", Number(overview.load.split(" ")[0]) / 4);
  byId("server-uptime").textContent = t("up {time}", { time: overview.uptime });
  byId("server-disks").textContent = overview.disks.map((disk) => `${disk.mount} ${t("{used} of {total}", { used: formatBytes(disk.used), total: formatBytes(disk.total) })}`).join("   ");
}

async function refresh(): Promise<void> {
  try {
    const [bots, overview] = await Promise.all([
      call<BotStatus[]>(CHANNELS.serverBots),
      call<ServerOverview>(CHANNELS.serverOverview)
    ]);
    renderList(byId("server-bots"), bots, buildBot);
    renderOverview(overview);
    byId("server-running").textContent = `${bots.filter((bot) => bot.health === "running").length}/${bots.length}`;
    setNeedle("server-running", bots.length ? bots.filter((bot) => bot.health === "running").length / bots.length : 0);
  } catch (error) {
    setStatus(t("could not reach the server: {error}", { error: String(error).replace(/^.*Error: /, "") }));
  }
}

async function reloadCaddy(): Promise<void> {
  setStatus(t("validating caddy..."));
  try {
    await call(CHANNELS.serverCaddy);
    setStatus(t("caddy reloaded"));
  } catch (error) {
    setStatus(String(error).replace(/^.*Error: /, ""));
  }
}

export function setupServerView(): void {
  byId("server-refresh").addEventListener("click", refresh);
  byId("server-caddy").addEventListener("click", reloadCaddy);
  byId("journal-close").addEventListener("click", () => (byId("server-journal").hidden = true));

  onViewShown("server", () => {
    refresh();
    pollTimer = window.setInterval(refresh, APP_SETTINGS.serverPollSeconds * 1000);
  });
  onViewHidden("server", () => window.clearInterval(pollTimer));
}
